/**
 * Normalization functions for job parameters
 * 
 * These functions ensure consistent formatting of job parameters
 * across all contexts (frontend public, frontend authenticated, backend)
 */

// ============================================
// Normalization Mappings
// ============================================

const COUNTRY_CODES: Record<string, string> = {
  US: "US",
  usa: "US",
  USA: "US",
  "united states": "US",
  CA: "CA",
  canada: "CA",
  UK: "GB",
  GB: "GB",
  "united kingdom": "GB",
  IN: "IN",
  india: "IN",
  DE: "DE",
  germany: "DE",
  AU: "AU",
  australia: "AU",
  FR: "FR",
  france: "FR",
};

const WORK_MODE_VALUES: Record<string, string> = {
  remote: "remote",
  Remote: "remote",
  hybrid: "hybrid",
  Hybrid: "hybrid",
  onsite: "onsite",
  "on-site": "onsite",
  "On-site": "onsite",
  Onsite: "onsite",
};

const JOB_TYPE_VALUES: Record<string, string> = {
  "full-time": "full-time",
  "Full Time": "full-time",
  fulltime: "full-time",
  "part-time": "part-time",
  "Part Time": "part-time",
  parttime: "part-time",
  contract: "contract",
  Contract: "contract",
  internship: "internship",
  Internship: "internship",
};

const SORT_BY_VALUES: Record<string, string> = {
  relevant: "most_relevant",
  high_epa: "high_cpa",
  high_cpa: "high_cpa",
  high_rpa: "high_cpa",
  high_epc: "high_cpc",
  high_cpc: "high_cpc",
  high_rpc: "high_cpc",
  newest: "newest",
  most_relevant: "most_relevant",
};

// ============================================
// Normalization Functions
// ============================================

/**
 * Normalize country code to uppercase ISO format
 * 
 * @param value - Country code or name (e.g., "us", "united states", "USA")
 * @returns Normalized country code (e.g., "US")
 * 
 * @example
 * normalizeCountryCode("us") // "US"
 * normalizeCountryCode("united states") // "US"
 * normalizeCountryCode("USA") // "US"
 */
export function normalizeCountryCode(value: string): string {
  if (!value) return "";
  const normalized = value.trim();
  return (
    COUNTRY_CODES[normalized] ||
    COUNTRY_CODES[normalized.toLowerCase()] ||
    normalized.toUpperCase()
  );
}

/**
 * Normalize work mode to lowercase API format
 * 
 * @param value - Work mode (e.g., "Remote", "On-Site", "Hybrid")
 * @returns Normalized work mode (e.g., "remote", "onsite", "hybrid")
 * 
 * @example
 * normalizeWorkMode("Remote") // "remote"
 * normalizeWorkMode("On-Site") // "onsite"
 * normalizeWorkMode("Hybrid") // "hybrid"
 */
export function normalizeWorkMode(value: string): string {
  if (!value) return "";
  return WORK_MODE_VALUES[value] || value.toLowerCase();
}

/**
 * Normalize job type to lowercase API format
 * 
 * @param value - Job type (e.g., "Full Time", "Part-Time", "Contract")
 * @returns Normalized job type (e.g., "full-time", "part-time", "contract")
 * 
 * @example
 * normalizeJobType("Full Time") // "full-time"
 * normalizeJobType("Part-Time") // "part-time"
 * normalizeJobType("Contract") // "contract"
 */
export function normalizeJobType(value: string): string {
  if (!value) return "";
  return JOB_TYPE_VALUES[value] || value.toLowerCase();
}

/**
 * Normalize niche category to kebab-case format
 * 
 * @param value - Niche category (e.g., "Software Development", "Marketing & Advertising")
 * @returns Normalized niche (e.g., "software-development", "marketing-advertising")
 * 
 * @example
 * normalizeNicheValue("Software Development") // "software-development"
 * normalizeNicheValue("Marketing & Advertising") // "marketing-advertising"
 * normalizeNicheValue("Data Analytics") // "data-analytics"
 */
export function normalizeNicheValue(value: string): string {
  if (!value) return "";
  
  return value
    .toLowerCase()
    .replace(/&/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Normalize sort_by parameter to API format
 * 
 * @param value - Sort option (e.g., "relevant", "high_epa", "newest")
 * @returns Normalized sort value (e.g., "most_relevant", "high_cpa", "newest")
 * 
 * @example
 * normalizeSortBy("relevant") // "most_relevant"
 * normalizeSortBy("high_epa") // "high_cpa"
 * normalizeSortBy("newest") // "newest"
 */
export function normalizeSortBy(value: string): string {
  if (!value) return "most_relevant";
  return SORT_BY_VALUES[value] || "most_relevant";
}
/**
 * Normalize text before checking whether required job facts are present.
 * Lowercases, replaces multiple spaces with single space, and trims.
 * 
 * @param value - String to normalize
 * @returns Normalized string
 */
export function normalizeForPresenceCheck(value: string): string {
  if (!value) return "";
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}
